"use client";

import mixpanel from "mixpanel-browser";
import { EnvService } from "@/services/Environment/Environment";
import { isNoxusInternal } from "@/utils/isNoxusInternal";

export enum ANALYTICS_EVENTS {
  // Misc
  PAGE_VIEW = "PAGE_VIEW",
  API_KEY_DELETE = "API_KEY_DELETE",
  // Integrations
  INTEGRATIONS_CONNECT_ACCOUNT = "INTEGRATIONS_CONNECT_ACCOUNT",
  INTEGRATIONS_DISCONNECT_ACCOUNT = "INTEGRATIONS_DISCONNECT_ACCOUNT",
  // Workflows
  WORKFLOW_CREATE = "WORKFLOW_CREATE",
  WORKFLOW_DUPLICATE = "WORKFLOW_DUPLICATE",
  WORKFLOW_DELETE = "WORKFLOW_DELETE",
  WORKFLOW_OPEN_RUN_MODAL = "WORKFLOW_OPEN_RUN_MODAL",
  WORKFLOW_RUN_START = "WORKFLOW_RUN_START",
  WORKFLOW_OPEN_PAST_RUN = "WORKFLOW_OPEN_PAST_RUN",
  WORKFLOW_ADVANCED_CONFIGS_TOGGLED = "WORKFLOW_ADVANCED_CONFIGS_TOGGLED",
  WORKFLOW_RESTORE = "WORKFLOW_RESTORE",
  WORKFLOW_VERSION_SAVE = "WORKFLOW_VERSION_SAVE",
  WORKFLOW_VERSION_OVERRIDE = "WORKFLOW_VERSION_OVERRIDE",
  WORKFLOW_VERSION_RESTORE = "WORKFLOW_VERSION_RESTORE",
  WORKFLOW_ADD_CONNECTION = "WORKFLOW_ADD_CONNECTION",
  WORKFLOW_DELETE_MODULE = "WORKFLOW_DELETE_MODULE",
  PREVIOUS_RUNS_FILTER = "PREVIOUS_RUNS_FILTER",
  // Agents
  AGENT_PRESET_SELECTOR_CLICK = "AGENT_PRESET_SELECTOR_CLICK",
  // Triggers
  TRIGGERS_NEW = "TRIGGERS_NEW",
  TRIGGERS_EDIT = "TRIGGERS_EDIT",
  TRIGGERS_DELETE = "TRIGGERS_DELETE",
  // Chat
  CHAT_MESSAGE_SEND = "CHAT_MESSAGE_SEND",
  CHAT_MESSAGE_EDIT = "MESSAGE_EDIT",
  CHAT_MESSAGE_EDIT_SEND = "CHAT_MESSAGE_EDIT_SEND",
  CHAT_MESSAGE_RETRY = "CHAT_MESSAGE_RETRY",
  CHAT_MESSAGE_REGENERATE = "CHAT_MESSAGE_REGENERATE",
  // Flows
  FLOWS_TAGS_CREATE = "FLOWS_TAGS_CREATE",
  FLOWS_TAGS_UPDATE = "FLOWS_TAGS_UPDATE",
  // Knowledge Bases
  DOCS_OPEN_FROM_EDITOR = "DOCS_OPEN_FROM_EDITOR", // wtf is this
  DOCS_MODULE_OPEN = "DOCS_MODULE_OPEN",
  // Knowledge Bases
  KNOWLEDGE_BASE_DELETE = "KNOWLEDGE_BASE_DELETE",
  // TODO
  // Done
  WORKFLOW_ADD_MODULE = "WORKFLOW_ADD_MODULE",
  KNOWLEDGE_BASE_CREATE = "KNOWLEDGE_BASE_CREATE",
  KNOWLEDGE_BASE_EDIT = "KNOWLEDGE_BASE_EDIT",
  KNOWLEDGE_BASE_INGESTION_JOB_CREATE = "KNOWLEDGE_BASE_INGESTION_JOB_CREATE",
  KNOWLEDGE_BASE_RETRY_DOCUMENT = "KNOWLEDGE_BASE_RETRY_DOCUMENT",
  KNOWLEDGE_BASE_RETRY_FAILED = "KNOWLEDGE_BASE_RETRY_FAILED",
  KNOWLEDGE_BASE_RETRY_ALL = "KNOWLEDGE_BASE_RETRY_ALL",
  // KB Sync
  KNOWLEDGE_BASE_SYNC_ENABLE = "KNOWLEDGE_BASE_SYNC_ENABLE",
  KNOWLEDGE_BASE_SYNC_DISABLE = "KNOWLEDGE_BASE_SYNC_DISABLE",
  KNOWLEDGE_BASE_SYNC_UPDATE = "KNOWLEDGE_BASE_SYNC_UPDATE",
  KNOWLEDGE_BASE_SYNC_FORCE = "KNOWLEDGE_BASE_SYNC_FORCE",
  COWORKER_CREATE = "COWORKER_CREATE",
  COWORKER_EDIT = "COWORKER_EDIT",
  COWORKER_DELETE = "COWORKER_DELETE",
  COWORKER_CHAT_NEW = "COWORKER_CHAT_NEW",
  COWORKER_VERSION_SAVE = "COWORKER_VERSION_SAVE",
  COWORKER_VERSION_OVERRIDE = "COWORKER_VERSION_OVERRIDE",
  COWORKER_VERSION_RESTORE = "COWORKER_VERSION_RESTORE",
  //Onboarding
  ONBOARDING_TEMPLATE_CARD_CLICK = "ONBOARDING_TEMPLATE_CARD_CLICK",
  ONBOARDING_DIALOG_CLOSE = "ONBOARDING_DIALOG_CLOSE",
  RESOURCES_CLICK = "RESOURCES_CLICK",
  //Templates
  TEMPLATES_CARD_CLICK = "TEMPLATES_CARD_CLICK",
  TEMPLATES_CONFIGURE_CLICK = "TEMPLATES_CONFIGURE_CLICK",
  TEMPLATES_TEST_NOW_CLICK = "TEMPLATES_TEST_NOW_CLICK",
  TEMPLATES_CONFIGURE_CREATE_CLICK = "TEMPLATES_CONFIGURE_CREATE_CLICK",
  //Billing
  BILLING_UPGRADE_CLICK = "BILLING_UPGRADE_CLICK",
  BILLING_TALK_TO_SALES_CLICK = "BILLING_TALK_TO_SALES_CLICK",
  BILLING_SIDENAV_BAR_CLICK = "BILLING_SIDENAV_BAR_CLICK",
  BILLING_TOPUP_HISTORY_CLICK = "BILLING_TOPUP_HISTORY_CLICK",
  BILLING_PAY_AS_YOU_GO_TOGGLE = "BILLING_PAY_AS_YOU_GO_TOGGLE",
  // Presets
  PRESET_CREATE = "PRESET_CREATE",
  PRESET_EDIT = "PRESET_EDIT",
  PRESET_DELETE = "PRESET_DELETE",
  PRESET_RESET = "PRESET_RESET",
  // Misc
  APP_DEPLOYED = "APP_DEPLOYED",
  // Feedback
  MESSAGE_FEEDBACK = "MESSAGE_FEEDBACK",
  MESSAGE_FEEDBACK_REMOVED = "MESSAGE_FEEDBACK_REMOVED",
  MESSAGE_FEEDBACK_EXPORTED = "MESSAGE_FEEDBACK_EXPORTED",
  // Bulk actions
  CONVERSATIONS_EXPORTED = "CONVERSATIONS_EXPORTED",
  CONVERSATIONS_DELETED = "CONVERSATIONS_DELETED",
  RUNS_EXPORTED = "RUNS_EXPORTED",
  RUNS_DELETED = "RUNS_DELETED",
  USAGE_HISTORY_DELETED = "USAGE_HISTORY_DELETED",
  CHAT_WIDGET_NEW_CONVERSATION_CLICK = "CHAT_WIDGET_NEW_CONVERSATION_CLICK",
  CHAT_WIDGET_FILE_UPLOAD_CLICK = "CHAT_WIDGET_FILE_UPLOAD_CLICK",
}

const BLACKLIST_EMAILS = ["pedroallenrevez@gmail.com"];
let isMixpanelInitialized = false;

export const initMixpanel = (user_id: string, user_name: string) => {
  const MIXPANEL_TOKEN = EnvService.getVar("MIXPANEL_TOKEN");
  if (!MIXPANEL_TOKEN) return;

  if (!isMixpanelInitialized) {
    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: "https://api-eu.mixpanel.com",
    });
    isMixpanelInitialized = true;
  }

  mixpanel.identify(user_id);
  mixpanel.people.set_once({
    email: user_id,
    name: user_name,
  });
};

export const analytics_track = (
  event: ANALYTICS_EVENTS,
  options?: { [key: string]: any }
) => {
  const environment = EnvService.getVar("DEPLOYMENT_ENV");
  const MIXPANEL_TOKEN = EnvService.getVar("MIXPANEL_TOKEN");

  const distinctId = isMixpanelInitialized ? mixpanel.get_distinct_id() : "";
  if (!distinctId) return;

  if (
    environment !== "prod" ||
    BLACKLIST_EMAILS.includes(distinctId) ||
    isNoxusInternal(distinctId) ||
    !MIXPANEL_TOKEN
  )
    return;
  try {
    mixpanel.track(event, options);
  } catch (e) {
    console.error("Error logging on mixpanel");
  }
};
